import { invoicePdfQueue } from './invoicePdfUpload';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Detention,
  GstMode,
  InvoiceIssueRequest,
  InvoiceReferences,
  InvoiceResponse,
  InvoiceSnapshot,
} from '../../api/types';
import {
  getPaymentTerms,
  issueInvoice,
  mapPaymentError,
  previewInvoice,
} from '../../api/endpoints/payments';
import { getShipmentById } from '../../api/endpoints/admin';
import { ExemptInvoiceForm } from './ExemptInvoiceForm';
import { istDateInput } from '../payments/paymentDates';
import { invoiceMoney } from './invoiceCopyText';
import styles from '../payments/Payments.module.css';
import { Button } from '../../components/ui/Button';
import { buttonClassName } from '../../components/ui/buttonClassName';
import { isDemo } from '../../demo/flag';
import { InvoiceReadinessChecklist } from './InvoiceReadinessChecklist';
import { formatStopMinutes } from '../shipments/stopLabels';
import { DialogHelp } from '../../help/DialogHelp';
const emptyReferences: InvoiceReferences = {
  lrNumber: null,
  lrDate: null,
  consignorInvoiceNo: null,
  packages: null,
  consignee: null,
  itemName: null,
};
export function GenerateInvoiceDialog({
  shipmentId,
  canEditBuyer = true,
  detention,
  onClose,
  onIssued,
}: {
  shipmentId: number;
  canEditBuyer?: boolean;
  /** Server detention (dnm-work#165 C3), shown read-only; no amount, no charge. Hidden when null or absent. */
  detention?: Detention | null;
  onClose: () => void;
  onIssued: (invoice: InvoiceResponse) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [clientRequestId, setClientRequestId] = useState(() => crypto.randomUUID());
  const [options, setOptions] = useState<string[]>([]);
  const [pair, setPair] = useState('');
  const [preview, setPreview] = useState<InvoiceSnapshot | null>(null);
  const [references, setReferences] = useState(emptyReferences);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingGstin, setMissingGstin] = useState(false);
  const [exempt, setExempt] = useState(false);
  const [exemptLocked, setExemptLocked] = useState(false);
  const [buyerId, setBuyerId] = useState<number | null>(null);
  const inFlight = useRef(false);
  const submittedRequest = useRef<InvoiceIssueRequest | null>(null);
  useEffect(() => {
    let active = true;
    Promise.all([getPaymentTerms(), previewInvoice(shipmentId)])
      .then(([terms, snapshot]) => {
        if (!active) return;
        const allowed = terms.invoiceGstOptions
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        const selected = allowed.find((v) => {
          const [mode, rate] = v.split(':');
          return mode === snapshot.tax.mode && Number(rate) === snapshot.tax.rate;
        });
        if (!selected)
          throw new Error('The default GST option changed. Close the dialog and try again.');
        setOptions(allowed);
        setPair(selected);
        setPreview(snapshot);
        setReferences(snapshot.references ?? emptyReferences);
      })
      .catch((e) => {
        if (active) {
          setError(mapPaymentError(e));
          setMissingGstin((e as { code?: string }).code === 'SHIPPER_GSTIN_REQUIRED');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [shipmentId]);
  useEffect(() => {
    if (!missingGstin) return;
    let active = true;
    void getShipmentById(shipmentId)
      .then((shipment) => {
        if (active) setBuyerId(shipment.shipperId);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [missingGstin, shipmentId]);
  useEffect(() => {
    if (!pair) return;
    let active = true;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      const [mode, rate] = pair.split(':');
      previewInvoice(shipmentId, mode as GstMode, Number(rate))
        .then((s) => {
          if (active) {
            setPreview(s);
            setLoading(false);
          }
        })
        .catch((e) => {
          if (active) {
            setError(mapPaymentError(e));
            setMissingGstin((e as { code?: string }).code === 'SHIPPER_GSTIN_REQUIRED');
            setLoading(false);
            setPreview(null);
          }
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [shipmentId, pair]);
  const validRefs =
    (references.lrDate == null || references.lrDate <= istDateInput()) &&
    (references.packages == null ||
      (Number.isInteger(references.packages) &&
        references.packages >= 0 &&
        references.packages <= 1000000));
  async function submit() {
    if (!preview || loading || busy || inFlight.current || !validRefs) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    const [mode, rate] = pair.split(':');
    // Freeze the request after the first attempt as well as its ID: a lost response must retry identically.
    submittedRequest.current ??= {
      clientRequestId,
      gstMode: mode as GstMode,
      gstRatePct: Number(rate),
      references,
    };
    try {
      const result = await issueInvoice(shipmentId, submittedRequest.current);
      void invoicePdfQueue.ensure(result, 'ORIGINAL');
      await onIssued(result);
    } catch (e) {
      setError(mapPaymentError(e));
      if ((e as { code?: string }).code === 'SHIPPER_GSTIN_REQUIRED') setMissingGstin(true);
      const failure = e as {
        status?: number;
        code?: string;
        response?: { status?: number };
      } | null;
      const status = failure?.status ?? failure?.response?.status;
      const uncertain =
        failure?.code === 'IDEMPOTENCY_RETRY' ||
        (status != null && status >= 500 && status < 600) ||
        (status == null && failure?.code == null);
      if (!uncertain) {
        submittedRequest.current = null;
        setClientRequestId(crypto.randomUUID());
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  const locked = busy || submittedRequest.current != null;
  return (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('invoices.generateInvoiceDialog.title')}
        className={styles.dialog}
      >
        <header className={styles.header}>
          <h2>{t('invoices.generateInvoiceDialog.title')}</h2>
          <div className={styles.actions}>
            <DialogHelp context="generate-invoice" title={t('invoices.generateInvoiceDialog.title')} />
            <Button variant="secondary" size="sm" disabled={busy} onClick={onClose}>
              {t('common.actions.close')}
            </Button>
          </div>
        </header>
        <div className={styles.body}>
          {/* Demo world only until the build issue wires rhino-api (#293). Warns, never blocks. */}
          {isDemo() && <InvoiceReadinessChecklist shipmentId={shipmentId} />}
          {detention && (
            <p>
              <strong>{t('stops.detention.invoiceLine')}</strong>
              {': '}
              {formatStopMinutes(t, detention.totalDetentionMinutes)}
              {' · '}
              {detention.final ? t('stops.detention.final') : t('stops.detention.soFar')}
            </p>
          )}
          <p>{t('invoices.generateInvoiceDialog.frozenNote')}</p>
          {missingGstin && (
            <section className={styles.card}>
              <p>{t('invoices.generateInvoiceDialog.missingGstin')}</p>
              {canEditBuyer && buyerId ? (
                <a
                  className={buttonClassName('primary', 'sm')}
                  href={`/users/${buyerId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('invoices.generateInvoiceDialog.getAddGstin')}
                </a>
              ) : (
                <p>
                  <strong>{t('invoices.generateInvoiceDialog.getAddGstinLabel')}</strong>{' '}
                  {t('invoices.generateInvoiceDialog.askManager', {
                    buyerRef: buyerId ? ` (#${buyerId})` : '',
                  })}
                </p>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={busy || exemptLocked}
                aria-expanded={exempt}
                aria-controls="exempt-invoice-details"
                onClick={() => setExempt(!exempt)}
              >
                {exempt ? 'Hide GST-exempt details' : 'Review GST-exempt option'}
              </Button>
            </section>
          )}
          {exempt && (
            <div id="exempt-invoice-details">
              <ExemptInvoiceForm
                shipmentId={shipmentId}
                clientRequestId={clientRequestId}
                references={references}
                onIssued={onIssued}
                onBusyChange={setBusy}
                onLockedChange={setExemptLocked}
              />
            </div>
          )}
          {!exempt && (
            <label className={styles.field}>
              {t('invoices.generateInvoiceDialog.gstOption')}
              <select
                aria-label={t('invoices.generateInvoiceDialog.gstOption')}
                value={pair}
                disabled={locked || !options.length}
                onChange={(e) => {
                  setPair(e.target.value);
                  setLoading(true);
                }}
              >
                {options.map((v) => (
                  <option key={v} value={v}>
                    {t('invoices.generateInvoiceDialog.gstRate', { rate: v.split(':')[1] })}{' '}
                    {v.startsWith('FCM')
                      ? 'charged by DNM'
                      : 'reverse charge: paid by the shipper to the Government'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <fieldset disabled={locked || exempt}>
            <legend>{t('invoices.generateInvoiceDialog.references')}</legend>
            <div className={styles.grid}>
              {(
                [
                  ['lrNumber', 'LR No.'],
                  ['lrDate', 'LR Date'],
                  ['consignorInvoiceNo', 'Consignor Invoice No.'],
                  ['packages', 'Packages'],
                  ['consignee', 'Consignee'],
                  ['itemName', 'Item'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className={styles.field}>
                  {label}
                  <input
                    aria-label={label}
                    type={key === 'lrDate' ? 'date' : key === 'packages' ? 'number' : 'text'}
                    maxLength={60}
                    min={key === 'packages' ? 0 : undefined}
                    max={
                      key === 'packages' ? 1000000 : key === 'lrDate' ? istDateInput() : undefined
                    }
                    value={references[key] ?? ''}
                    onChange={(e) =>
                      setReferences({
                        ...references,
                        [key]:
                          key === 'packages'
                            ? e.target.value === ''
                              ? null
                              : Number(e.target.value)
                            : e.target.value || null,
                      })
                    }
                  />
                </label>
              ))}
            </div>
          </fieldset>
          {!validRefs && <p role="alert">{t('invoices.generateInvoiceDialog.invalidRefs')}</p>}
          {loading ? (
            <p role="status">{t('invoices.generateInvoiceDialog.updating')}</p>
          ) : (
            preview && (
              <div className={styles.card}>
                <p>
                  {t('invoices.generateInvoiceDialog.buyer')} {preview.buyer.companyName} ·{' '}
                  {t('invoices.generateInvoiceDialog.gstin')} {preview.buyer.gstin}
                </p>
                <p>
                  {t('invoices.generateInvoiceDialog.taxable', {
                    amount: invoiceMoney(preview.tax.taxableAmount),
                  })}
                </p>
                {preview.tax.mode === 'FCM' &&
                  preview.tax.lines.map((l) => (
                    <p key={l.name}>
                      {l.name} @ {l.rate} %: {invoiceMoney(l.amount)}
                    </p>
                  ))}
                <p>{preview.tax.note}</p>
                <strong>
                  {t('invoices.generateInvoiceDialog.grandTotal', {
                    amount: invoiceMoney(preview.tax.grandTotal),
                  })}
                </strong>
              </div>
            )
          )}
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          {submittedRequest.current && !busy && (
            <p>{t('invoices.generateInvoiceDialog.retryNote')}</p>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={loading || busy || !preview || !validRefs || exempt}
            onClick={submit}
          >
            {busy ? 'Issuing…' : submittedRequest.current ? 'Retry issue' : 'Issue invoice'}
          </Button>
        </div>
      </section>
    </div>
  );
}
